import { useState } from "react";
import PropTypes from "prop-types";
import { Autocomplete, Box, Checkbox, TextField, CircularProgress, Typography, useTheme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { useRewampT } from "../../../localisation";
import { dropdownPartSx } from "../../../design/recipes/dropdown";
import DropdownArrowIcon from "./DropdownArrowIcon";
import { useFilterCellAnchor } from "./FilterFieldCell";
// A key only counts when the option actually has it — otherwise `undefined === undefined` matched everything.
const sameKey = (a, b, key) => a?.[key] != null && a[key] === b?.[key];
const defaultIsOptionEqualToValue = (option, val) =>
  sameKey(option, val, "id") || sameKey(option, val, "code") || sameKey(option, val, "value") || option === val;
const defaultGetOptionLabel = (option) => option?.label || option?.name || String(option);

export default function FilterMultiSelectField({
  controlId,
  labelKey,
  label,
  options,
  value,
  onChange,
  loading,
  getOptionLabel,
  isOptionEqualToValue,
  placeholderKey,
  maxSelections,
  dropdownVariant,
}) {
  const tRewamp = useRewampT();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { anchorEl: cellEl, gap } = useFilterCellAnchor();
  const resolvedLabel = label || (labelKey ? tRewamp(labelKey) : "");
  const placeholder = placeholderKey ? tRewamp(placeholderKey) : resolvedLabel;
  // Opt-in dropdown variant (recipes/dropdown.js); without it the stock Autocomplete chrome renders.
  // Option parts go on the listbox so they out-specify MUI's own `.MuiAutocomplete-option` rule.
  const optionSx = dropdownPartSx(dropdownVariant, "option");
  const optionSelectedSx = dropdownPartSx(dropdownVariant, "optionSelected");
  const triggerSx = dropdownPartSx(dropdownVariant, "trigger");
  const tickSx = dropdownPartSx(dropdownVariant, "tick");
  const tickSelectedSx = dropdownPartSx(dropdownVariant, "tickSelected");
  const popperZ = dropdownPartSx(dropdownVariant, "popper")?.(theme);
  const atLimit = maxSelections != null && value.length >= maxSelections;
  const isSelected = (option) => value.some((v) => isOptionEqualToValue(option, v));
  // Selection reads as one line of text, never chips; it gives way to the search input while open.
  const summary = !open && value.length ? value.map(getOptionLabel).join(", ") : null;
  const slotProps = dropdownVariant
    ? {
        paper: { variant: "filterDropdownPanel" },
        popper: {
          ...(popperZ && { sx: popperZ }),
          ...(cellEl && { anchorEl: cellEl, placement: "bottom-start", style: { width: cellEl.clientWidth } }),
          // Popper offset (not paper margin) so the gap stays on the right side when the window flips up.
          modifiers: [{ name: "offset", options: { offset: [0, parseFloat(theme.spacing(gap))] } }],
        },
      }
    : undefined;
  const listboxProps = dropdownVariant
    ? {
        sx: (theme) => ({
          "& .MuiAutocomplete-option": optionSx(theme),
          "& .MuiAutocomplete-option[aria-selected='true']": optionSelectedSx(theme),
        }),
      }
    : undefined;

  return (
    <Autocomplete
      slotProps={slotProps}
      ListboxProps={listboxProps}
      popupIcon={dropdownVariant ? <DropdownArrowIcon /> : undefined}
      sx={triggerSx ? { "& .MuiOutlinedInput-root": triggerSx } : undefined}
      multiple
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      openOnFocus
      id={controlId}
      options={options}
      value={value}
      onChange={(e, next) => {
        if (maxSelections != null && next.length > maxSelections) return;
        onChange(next);
      }}
      getOptionDisabled={(option) => atLimit && !isSelected(option)}
      loading={loading}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      disableCloseOnSelect
      renderTags={() => null}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          {tickSx ? (
            <Box component="span" aria-hidden="true" sx={[tickSx, selected && tickSelectedSx]}>
              <CheckIcon />
            </Box>
          ) : (
            <Checkbox checked={selected} sx={{ mr: 1 }} />
          )}
          {getOptionLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          placeholder={summary ? "" : placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: summary ? (
              <Typography component="span" noWrap onClick={() => setOpen(true)} sx={{ cursor: "text", fontSize: "inherit", lineHeight: "inherit", minWidth: 0, flex: "0 1 auto" }} title={summary}>
                {summary}
              </Typography>
            ) : (
              params.InputProps.startAdornment
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

FilterMultiSelectField.propTypes = {
  controlId: PropTypes.string,
  labelKey: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  getOptionLabel: PropTypes.func,
  isOptionEqualToValue: PropTypes.func,
  placeholderKey: PropTypes.string,
  /** Unselected options are disabled once this many are selected; omit for no cap. */
  maxSelections: PropTypes.number,
  /** Dropdown variant from dropdownVocabulary.js; omit for the stock Autocomplete chrome. */
  dropdownVariant: PropTypes.string,
};

FilterMultiSelectField.defaultProps = {
  maxSelections: null,
  controlId: undefined,
  labelKey: null,
  label: null,
  loading: false,
  getOptionLabel: defaultGetOptionLabel,
  isOptionEqualToValue: defaultIsOptionEqualToValue,
  placeholderKey: null,
  dropdownVariant: undefined,
};
